import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Button, Badge, ProgressBar, ConfirmModal } from '../ui';
import WorkerStatusBanner from './WorkerStatusBanner';
import useJobProgress, { isTerminal } from './useJobProgress';
import { createEpicorJob, cancelEpicorJob } from '../../services/epicorApi';
import { SYNC_TARGETS } from '../../constants/markets';

// Staging targets only. Production can never be selected here — and even if a
// prod payload were forged, the API rejects it (422) and the worker-side
// service refuses any environment other than 'staging'.
const STAGING_TARGETS = SYNC_TARGETS.filter((t) => t.environment === 'staging');

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n ?? '0');
const num = (v) => Number(v) || 0;

// Persist the tracked job per target so navigating away and back re-attaches.
const STORE_PREFIX = 'waterco.companyCleanup.';
const jobStore = {
  get(key) {
    try {
      const raw = window.localStorage.getItem(STORE_PREFIX + key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && parsed.jobId ? parsed : null;
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(STORE_PREFIX + key, JSON.stringify(value));
    } catch (e) {
      /* ignore */
    }
  },
  clear(key) {
    try {
      window.localStorage.removeItem(STORE_PREFIX + key);
    } catch (e) {
      /* ignore */
    }
  },
};

const cleanupProgress = ({ counters, status }) => {
  const c = counters || {};
  const running = status === 'running' || status === 'pending';
  const total = num(c.companies_total);
  const deleted = num(c.companies_deleted);
  const segs = [`${fmt(deleted)} deleted`];
  if (num(c.failed)) segs.push(`${fmt(num(c.failed))} failed`);
  if (num(c.customers_deleted)) segs.push(`${fmt(num(c.customers_deleted))} customers removed`);
  if (num(c.mappings_cleared)) segs.push(`${fmt(num(c.mappings_cleared))} mappings cleared`);
  if (total > 0) {
    const pct = Math.min(100, Math.round((deleted / total) * 100));
    return {
      pct,
      indeterminate: false,
      label: `${segs.join(' · ')} · ${fmt(deleted)}/${fmt(total)} (${pct}%)`,
    };
  }
  return { pct: 0, indeterminate: running, label: segs.join(' · ') };
};

const CleanupCard = ({ target }) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [jobLinks, setJobLinks] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [lastMode, setLastMode] = useState(null); // 'preview' | 'delete'

  const { market } = target;
  const running = busy || ['pending', 'running'].includes(progress.status);
  const storageKey = `cleanup:${target.id}`;

  // Resume on mount: re-attach to a job that was running when we left.
  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      setLastMode(saved.mode || null);
      progress.track(saved.jobId, 'company_cleanup');
    }
    // Mount-once on the stable storageKey; progress.track/setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist while live; clear once terminal.
  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, mode: lastMode });
    }
  }, [progress.id, progress.status, lastMode, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      await cancelEpicorJob(progress.id);
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [progress.id]);

  const run = useCallback(
    async (dryRun) => {
      setError(null);
      setJobLinks([]);
      setCancelling(false);
      setLastMode(dryRun ? 'preview' : 'delete');
      setBusy(true);
      try {
        const { job_id } = await createEpicorJob({
          stage: 'delete_companies',
          companies: [market],
          environment: 'staging',
          dry_run: dryRun,
        });
        setJobLinks([{ id: job_id, label: `#${job_id}` }]);
        progress.track(job_id, 'company_cleanup', { onClose: () => setBusy(false) });
        setBusy(false);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to enqueue');
        setBusy(false);
      }
    },
    [market, progress],
  );

  const c = progress.counters || {};
  const term = isTerminal(progress.status);
  const base = cleanupProgress({ counters: c, status: progress.status });
  let { pct, indeterminate, label } = base;
  let tone;
  if (term) {
    indeterminate = false;
    if (progress.status === 'succeeded') {
      pct = 100;
      tone = num(c.failed) || num(c.companies_remaining) ? 'warning' : 'success';
      label =
        lastMode === 'preview'
          ? `Preview: ${fmt(num(c.companies_total))} companies would be deleted`
          : `${fmt(num(c.companies_deleted))} deleted · ${fmt(num(c.companies_remaining))} remaining`;
    } else {
      tone = 'danger';
      label = `Job ${progress.status}`;
    }
  }

  const showVerification = term && progress.status === 'succeeded' && lastMode === 'delete';
  const wasCancelled = term && progress.status === 'cancelled';

  return (
    <Card
      title={`${target.label}`}
      desc={`Delete ALL B2B companies from BigCommerce ${target.label}, including their admin storefront customers and the middleware mapping rows. Irreversible.`}
      className="manual-sync-card"
    >
      {progress.id && <ProgressBar pct={pct} indeterminate={indeterminate} label={label} tone={tone} />}
      {jobLinks.length > 0 && (
        <p className="manual-sync-links">
          {progress.status && (
            <Badge variant={term ? (progress.status === 'succeeded' ? 'success' : 'danger') : 'info'}>
              {progress.status}
            </Badge>
          )}{' '}
          {jobLinks.map((j) => (
            <Link key={j.id} to="/jobs/history?queue=epicor">{j.label}</Link>
          ))}
        </p>
      )}

      {term && lastMode === 'preview' && progress.status === 'succeeded' && (
        <p className="banner info manual-sync-note">
          Dry run: <strong>{fmt(num(c.companies_total))}</strong> companies and{' '}
          <strong>{fmt(num(c.admin_customers_total))}</strong> admin customers would be deleted
          from {target.label}. Nothing was changed.
        </p>
      )}

      {showVerification && (
        num(c.companies_remaining) === 0 && !num(c.failed) ? (
          <p className="banner info manual-sync-note">
            Verified: 0 companies remain in {target.label}.
          </p>
        ) : (
          <div className="banner warning" role="alert">
            {fmt(num(c.companies_remaining))} companies remain
            {num(c.failed) ? ` (${fmt(num(c.failed))} deletes failed)` : ''}. Re-run the delete —
            it is safe to repeat (already-deleted companies are skipped).
          </div>
        )
      )}

      {wasCancelled && (
        <div className="banner warning" role="alert">
          Cancelled mid-wipe: some companies may already be deleted while middleware mappings were
          left uncleared. Re-run the delete to finish — repeating is safe.
        </div>
      )}

      {cancelling && !term && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button variant="secondary" onClick={() => run(true)} disabled={running}>
          {running && lastMode === 'preview' ? 'Running…' : 'Preview (dry run)'}
        </Button>
        <Button variant="danger" onClick={() => setConfirming(true)} disabled={running}>
          {running && lastMode === 'delete' ? 'Deleting…' : 'Delete all companies'}
        </Button>
        {progress.id && !term && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>

      {confirming && (
        <ConfirmModal
          title={`Delete ALL companies in ${target.label}?`}
          body={`This permanently deletes every B2B company in BigCommerce ${target.label}, their admin storefront customers, and the middleware mapping rows. This cannot be undone.`}
          confirmLabel="Delete all companies"
          danger
          requireText={target.label}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            run(false);
          }}
        />
      )}
    </Card>
  );
};

const CompanyCleanup = () => {
  const targets = useMemo(() => STAGING_TARGETS, []);
  return (
    <div className="sync-page manual-syncs">
      <PageHeader
        title="Company Cleanup"
        sub="Wipe all B2B companies from a staging BigCommerce store before re-running a company push. Staging only — production cleanup is not supported and is blocked in the API and the worker."
      />

      <div className="banner warning" role="note">
        Deletion is irreversible. Only <strong>staging</strong> stores can be targeted: the API
        rejects any other environment and the worker independently verifies the store is not
        production before deleting.
      </div>

      {targets.map((t) => (
        <React.Fragment key={t.id}>
          <WorkerStatusBanner marketCode={t.market} environment={t.environment} />
        </React.Fragment>
      ))}

      <div className="manual-sync-grid">
        {targets.map((t) => (
          <CleanupCard key={t.id} target={t} />
        ))}
      </div>
    </div>
  );
};

export default CompanyCleanup;
